'use client';

import { useState } from 'react';
import type {
  PresentationSlide,
  SlideType,
  KPIData,
  RiskOpportunity,
  InvestmentItem,
  RoadmapItem,
  SLIDE_TYPE_CONFIG,
} from '@/lib/board-presentation/types';

interface SlideEditorProps {
  slide: PresentationSlide;
  onUpdate: (slide: PresentationSlide) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

export function SlideEditor({
  slide,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: SlideEditorProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const updateContent = (updates: Partial<typeof slide.content>) => {
    onUpdate({
      ...slide,
      content: { ...slide.content, ...updates },
    });
  };

  const renderTitleSlide = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-foreground-secondary mb-1">Title</label>
        <input
          type="text"
          value={slide.content.title || ''}
          onChange={(e) => updateContent({ title: e.target.value })}
          placeholder="Q1 2025 Technology Update"
          className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
        />
      </div>
      <div>
        <label className="block text-sm text-foreground-secondary mb-1">Subtitle</label>
        <input
          type="text"
          value={slide.content.subtitle || ''}
          onChange={(e) => updateContent({ subtitle: e.target.value })}
          placeholder="Board of Directors Meeting"
          className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
        />
      </div>
      <div>
        <label className="block text-sm text-foreground-secondary mb-1">Date</label>
        <input
          type="text"
          value={slide.content.date || ''}
          onChange={(e) => updateContent({ date: e.target.value })}
          placeholder="January 2025"
          className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
        />
      </div>
    </div>
  );

  const renderExecutiveSummary = () => {
    const highlights = slide.content.highlights || [''];
    const concerns = slide.content.concerns || [''];

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-foreground-secondary mb-2">Key Highlights</label>
          {highlights.map((highlight, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <input
                type="text"
                value={highlight}
                onChange={(e) => {
                  const newHighlights = [...highlights];
                  newHighlights[idx] = e.target.value;
                  updateContent({ highlights: newHighlights });
                }}
                placeholder="Highlight point..."
                className="flex-1 px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
              />
              <button
                onClick={() => {
                  const newHighlights = highlights.filter((_, i) => i !== idx);
                  updateContent({ highlights: newHighlights.length ? newHighlights : [''] });
                }}
                className="px-2 text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            onClick={() => updateContent({ highlights: [...highlights, ''] })}
            className="text-sm text-accent hover:underline"
          >
            + Add highlight
          </button>
        </div>
        <div>
          <label className="block text-sm text-foreground-secondary mb-2">Concerns/Challenges</label>
          {concerns.map((concern, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <input
                type="text"
                value={concern}
                onChange={(e) => {
                  const newConcerns = [...concerns];
                  newConcerns[idx] = e.target.value;
                  updateContent({ concerns: newConcerns });
                }}
                placeholder="Concern or challenge..."
                className="flex-1 px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
              />
              <button
                onClick={() => {
                  const newConcerns = concerns.filter((_, i) => i !== idx);
                  updateContent({ concerns: newConcerns.length ? newConcerns : [''] });
                }}
                className="px-2 text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            onClick={() => updateContent({ concerns: [...concerns, ''] })}
            className="text-sm text-accent hover:underline"
          >
            + Add concern
          </button>
        </div>
      </div>
    );
  };

  const renderKPIs = () => {
    const kpis: KPIData[] = slide.content.kpis || [];

    const addKPI = () => {
      updateContent({
        kpis: [...kpis, { name: '', value: '', trend: 'stable' as const }],
      });
    };

    const updateKPI = (idx: number, updates: Partial<KPIData>) => {
      const newKpis = [...kpis];
      newKpis[idx] = { ...newKpis[idx], ...updates };
      updateContent({ kpis: newKpis });
    };

    const removeKPI = (idx: number) => {
      updateContent({ kpis: kpis.filter((_, i) => i !== idx) });
    };

    return (
      <div className="space-y-4">
        {kpis.map((kpi, idx) => (
          <div key={idx} className="p-4 border border-border-muted rounded-lg">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Metric Name</label>
                <input
                  type="text"
                  value={kpi.name}
                  onChange={(e) => updateKPI(idx, { name: e.target.value })}
                  placeholder="Sprint Velocity"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Value</label>
                <input
                  type="text"
                  value={kpi.value}
                  onChange={(e) => updateKPI(idx, { value: e.target.value })}
                  placeholder="42"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Trend</label>
                <select
                  value={kpi.trend}
                  onChange={(e) => updateKPI(idx, { trend: e.target.value as 'up' | 'down' | 'stable' })}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                >
                  <option value="up">↑ Up</option>
                  <option value="down">↓ Down</option>
                  <option value="stable">→ Stable</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Target</label>
                <input
                  type="text"
                  value={kpi.target || ''}
                  onChange={(e) => updateKPI(idx, { target: e.target.value })}
                  placeholder="50"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Change %</label>
                <input
                  type="number"
                  value={kpi.changePercent || ''}
                  onChange={(e) => updateKPI(idx, { changePercent: parseFloat(e.target.value) || undefined })}
                  placeholder="+15"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <button
              onClick={() => removeKPI(idx)}
              className="mt-3 text-sm text-red-400 hover:text-red-300"
            >
              Remove KPI
            </button>
          </div>
        ))}
        <button onClick={addKPI} className="text-sm text-accent hover:underline">
          + Add KPI
        </button>
      </div>
    );
  };

  const renderRisksOpportunities = () => {
    const items: RiskOpportunity[] = slide.content.items || [];

    const addItem = (type: 'risk' | 'opportunity') => {
      updateContent({
        items: [
          ...items,
          {
            id: `item-${Date.now()}`,
            type,
            title: '',
            description: '',
            impact: 'medium' as const,
            likelihood: 'medium' as const,
          },
        ],
      });
    };

    const updateItem = (idx: number, updates: Partial<RiskOpportunity>) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], ...updates };
      updateContent({ items: newItems });
    };

    const removeItem = (idx: number) => {
      updateContent({ items: items.filter((_, i) => i !== idx) });
    };

    return (
      <div className="space-y-4">
        {items.map((item, idx) => (
          <div
            key={item.id}
            className={`p-4 border rounded-lg ${
              item.type === 'risk' ? 'border-red-500/30 bg-red-500/5' : 'border-green-500/30 bg-green-500/5'
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{item.type === 'risk' ? '⚠️' : '✨'}</span>
              <span className="text-sm font-medium text-foreground-secondary capitalize">{item.type}</span>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                value={item.title}
                onChange={(e) => updateItem(idx, { title: e.target.value })}
                placeholder="Title..."
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
              />
              <textarea
                value={item.description}
                onChange={(e) => updateItem(idx, { description: e.target.value })}
                placeholder="Description..."
                rows={2}
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-foreground-tertiary mb-1">Impact</label>
                  <select
                    value={item.impact}
                    onChange={(e) => updateItem(idx, { impact: e.target.value as 'high' | 'medium' | 'low' })}
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-foreground-tertiary mb-1">Likelihood</label>
                  <select
                    value={item.likelihood}
                    onChange={(e) => updateItem(idx, { likelihood: e.target.value as 'high' | 'medium' | 'low' })}
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              {item.type === 'risk' && (
                <div>
                  <label className="block text-xs text-foreground-tertiary mb-1">Mitigation</label>
                  <textarea
                    value={item.mitigation || ''}
                    onChange={(e) => updateItem(idx, { mitigation: e.target.value })}
                    placeholder="How to mitigate this risk..."
                    rows={2}
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                  />
                </div>
              )}
            </div>
            <button
              onClick={() => removeItem(idx)}
              className="mt-3 text-sm text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex gap-3">
          <button onClick={() => addItem('risk')} className="text-sm text-red-400 hover:underline">
            + Add Risk
          </button>
          <button onClick={() => addItem('opportunity')} className="text-sm text-green-400 hover:underline">
            + Add Opportunity
          </button>
        </div>
      </div>
    );
  };

  const renderInvestmentRequest = () => {
    const investments: InvestmentItem[] = slide.content.investments || [];

    const addInvestment = () => {
      updateContent({
        investments: [
          ...investments,
          {
            id: `inv-${Date.now()}`,
            title: '',
            amount: 0,
            currency: 'USD',
            category: 'tooling' as const,
            justification: '',
          },
        ],
      });
    };

    const updateInvestment = (idx: number, updates: Partial<InvestmentItem>) => {
      const newInvestments = [...investments];
      newInvestments[idx] = { ...newInvestments[idx], ...updates };
      updateContent({ investments: newInvestments });
    };

    const removeInvestment = (idx: number) => {
      updateContent({ investments: investments.filter((_, i) => i !== idx) });
    };

    const totalAmount = investments.reduce((sum, inv) => sum + (inv.amount || 0), 0);

    return (
      <div className="space-y-4">
        <div className="p-4 bg-accent-muted border border-border rounded-lg">
          <div className="text-sm text-foreground-tertiary">Total Investment Request</div>
          <div className="text-2xl font-bold text-accent">
            ${totalAmount.toLocaleString()}
          </div>
        </div>
        {investments.map((inv, idx) => (
          <div key={inv.id} className="p-4 border border-border-muted rounded-lg">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Title</label>
                <input
                  type="text"
                  value={inv.title}
                  onChange={(e) => updateInvestment(idx, { title: e.target.value })}
                  placeholder="Cloud Infrastructure Upgrade"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Amount ($)</label>
                <input
                  type="number"
                  value={inv.amount || ''}
                  onChange={(e) => updateInvestment(idx, { amount: parseFloat(e.target.value) || 0 })}
                  placeholder="50000"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Category</label>
                <select
                  value={inv.category}
                  onChange={(e) => updateInvestment(idx, { category: e.target.value as InvestmentItem['category'] })}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                >
                  <option value="infrastructure">Infrastructure</option>
                  <option value="tooling">Tooling</option>
                  <option value="headcount">Headcount</option>
                  <option value="training">Training</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Timeline</label>
                <input
                  type="text"
                  value={inv.timeline || ''}
                  onChange={(e) => updateInvestment(idx, { timeline: e.target.value })}
                  placeholder="Q1-Q2 2025"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Justification</label>
              <textarea
                value={inv.justification}
                onChange={(e) => updateInvestment(idx, { justification: e.target.value })}
                placeholder="Why this investment is needed..."
                rows={2}
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
              />
            </div>
            <button
              onClick={() => removeInvestment(idx)}
              className="mt-3 text-sm text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ))}
        <button onClick={addInvestment} className="text-sm text-accent hover:underline">
          + Add Investment Item
        </button>
      </div>
    );
  };

  const renderRoadmap = () => {
    const items: RoadmapItem[] = slide.content.roadmapItems || [];

    const addItem = () => {
      updateContent({
        roadmapItems: [
          ...items,
          {
            id: `road-${Date.now()}`,
            title: '',
            quarter: 'Q1 2025',
            status: 'planned' as const,
          },
        ],
      });
    };

    const updateItem = (idx: number, updates: Partial<RoadmapItem>) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], ...updates };
      updateContent({ roadmapItems: newItems });
    };

    const removeItem = (idx: number) => {
      updateContent({ roadmapItems: items.filter((_, i) => i !== idx) });
    };

    const statusColors: Record<RoadmapItem['status'], string> = {
      completed: 'bg-green-500',
      in_progress: 'bg-blue-500',
      planned: 'bg-cream/30',
      at_risk: 'bg-red-500',
    };

    return (
      <div className="space-y-4">
        {items.map((item, idx) => (
          <div key={item.id} className="p-4 border border-border-muted rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full ${statusColors[item.status]}`} />
              <span className="text-sm text-foreground-tertiary capitalize">{item.status.replace('_', ' ')}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Title</label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => updateItem(idx, { title: e.target.value })}
                  placeholder="API V2 Launch"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Quarter</label>
                <input
                  type="text"
                  value={item.quarter}
                  onChange={(e) => updateItem(idx, { quarter: e.target.value })}
                  placeholder="Q1 2025"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Status</label>
                <select
                  value={item.status}
                  onChange={(e) => updateItem(idx, { status: e.target.value as RoadmapItem['status'] })}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                >
                  <option value="completed">Completed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="planned">Planned</option>
                  <option value="at_risk">At Risk</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Team</label>
                <input
                  type="text"
                  value={item.team || ''}
                  onChange={(e) => updateItem(idx, { team: e.target.value })}
                  placeholder="Platform Team"
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Description</label>
              <textarea
                value={item.description || ''}
                onChange={(e) => updateItem(idx, { description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
              />
            </div>
            <button
              onClick={() => removeItem(idx)}
              className="mt-3 text-sm text-red-400 hover:text-red-300"
            >
              Remove
            </button>
          </div>
        ))}
        <button onClick={addItem} className="text-sm text-accent hover:underline">
          + Add Roadmap Item
        </button>
      </div>
    );
  };

  const renderCustomSlide = () => (
    <div>
      <label className="block text-sm text-foreground-secondary mb-1">Content (Markdown)</label>
      <textarea
        value={slide.content.customMarkdown || ''}
        onChange={(e) => updateContent({ customMarkdown: e.target.value })}
        placeholder="Enter your custom content in markdown format..."
        rows={8}
        className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground font-mono text-sm"
      />
    </div>
  );

  const renderContent = () => {
    switch (slide.slide_type) {
      case 'title':
        return renderTitleSlide();
      case 'executive_summary':
        return renderExecutiveSummary();
      case 'kpis':
        return renderKPIs();
      case 'risks_opportunities':
        return renderRisksOpportunities();
      case 'investment_request':
        return renderInvestmentRequest();
      case 'roadmap':
        return renderRoadmap();
      case 'team_update':
        return renderExecutiveSummary(); // Similar structure
      case 'custom':
        return renderCustomSlide();
      default:
        return <p className="text-foreground-tertiary">Unknown slide type</p>;
    }
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-cream/5 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">
            {slide.slide_type === 'title' && '📋'}
            {slide.slide_type === 'executive_summary' && '📊'}
            {slide.slide_type === 'kpis' && '📈'}
            {slide.slide_type === 'risks_opportunities' && '⚠️'}
            {slide.slide_type === 'investment_request' && '💰'}
            {slide.slide_type === 'roadmap' && '🗺️'}
            {slide.slide_type === 'team_update' && '👥'}
            {slide.slide_type === 'custom' && '✏️'}
          </span>
          <div>
            <div className="font-medium text-foreground">{slide.title}</div>
            <div className="text-xs text-foreground-tertiary capitalize">
              {slide.slide_type.replace('_', ' ')}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canMoveUp && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp?.();
              }}
              className="p-1 text-foreground-tertiary hover:text-foreground"
            >
              ↑
            </button>
          )}
          {canMoveDown && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown?.();
              }}
              className="p-1 text-foreground-tertiary hover:text-foreground"
            >
              ↓
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this slide?')) onDelete();
            }}
            className="p-1 text-red-400 hover:text-red-300"
          >
            🗑️
          </button>
          <span className="text-foreground-tertiary">{isExpanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 border-t border-border-muted">
          <div className="mb-4">
            <label className="block text-sm text-foreground-secondary mb-1">Slide Title</label>
            <input
              type="text"
              value={slide.title}
              onChange={(e) => onUpdate({ ...slide, title: e.target.value })}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground"
            />
          </div>
          {renderContent()}
          <div className="mt-4">
            <label className="block text-sm text-foreground-secondary mb-1">Speaker Notes</label>
            <textarea
              value={slide.notes || ''}
              onChange={(e) => onUpdate({ ...slide, notes: e.target.value })}
              placeholder="Notes for the presenter..."
              rows={2}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}
